import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { expensifyService } from '@/services/integrations/expensifyService';
import { hiveService } from '@/services/integrations/hiveService';
import {
  Receipt,
  Clock,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  DollarSign,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';

interface ProjectIntegrationsProps {
  projectId: string;
  organizationId: string;
}

export default function ProjectIntegrations({ projectId, organizationId }: ProjectIntegrationsProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState<'expensify' | 'hive' | null>(null);
  
  // Integration states
  const [expensifyEnabled, setExpensifyEnabled] = useState(false);
  const [hiveEnabled, setHiveEnabled] = useState(false);
  const [hiveProjectId, setHiveProjectId] = useState('');
  const [lastExpensifySync, setLastExpensifySync] = useState<Date | null>(null);
  const [lastHiveSync, setLastHiveSync] = useState<Date | null>(null);

  // Load integration settings
  useState(() => {
    loadIntegrationSettings();
  }, [projectId]);

  const loadIntegrationSettings = async () => {
    try {
      const { data: project } = await supabase
        .from('projects')
        .select('external_sync_enabled, hive_project_id, external_sync_date')
        .eq('id', projectId)
        .single();

      if (project) {
        setHiveEnabled(project.external_sync_enabled || false);
        setHiveProjectId(project.hive_project_id || '');
        if (project.external_sync_date) {
          setLastHiveSync(new Date(project.external_sync_date));
        }
      }

      // Check if Expensify is configured for organization
      const { data: expensifyConfig } = await supabase
        .from('integration_configs')
        .select('is_active')
        .eq('organization_id', organizationId)
        .eq('integration_type', 'expensify')
        .single();

      setExpensifyEnabled(expensifyConfig?.is_active || false);
    } catch (error) {
      console.error('Error loading integration settings:', error);
    }
  };

  // Sync Expensify expenses
  const syncExpensifyExpenses = async () => {
    setSyncing('expensify');
    try {
      await expensifyService.initialize(organizationId);
      const result = await expensifyService.syncProjectExpenses(projectId);
      
      setLastExpensifySync(new Date());
      
      toast({
        title: 'Expensify Sync Complete',
        description: `Synced ${result.synced_count} expenses. ${result.failed_count} failed.`,
      });
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync Expensify expenses',
        variant: 'destructive',
      });
    } finally {
      setSyncing(null);
    }
  };

  // Sync HIVE time entries
  const syncHiveTimeEntries = async () => {
    setSyncing('hive');
    try {
      await hiveService.initialize(organizationId);
      const result = await hiveService.importTimeEntries(projectId);
      
      setLastHiveSync(new Date());
      
      const successCount = result.filter(r => r.success).length;
      const failedCount = result.filter(r => !r.success).length;
      
      toast({
        title: 'HIVE Sync Complete',
        description: `Synced ${successCount} time entries. ${failedCount} failed.`,
      });
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync HIVE time entries',
        variant: 'destructive',
      });
    } finally {
      setSyncing(null);
    }
  };

  // Toggle HIVE integration
  const toggleHiveIntegration = async (enabled: boolean) => {
    setLoading(true);
    try {
      if (enabled && !hiveProjectId) {
        toast({
          title: 'HIVE Project ID Required',
          description: 'Please enter the HIVE project ID to enable integration',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from('projects')
        .update({
          external_sync_enabled: enabled,
          hive_project_id: enabled ? hiveProjectId : null,
        })
        .eq('id', projectId);

      if (error) throw error;

      setHiveEnabled(enabled);
      
      toast({
        title: enabled ? 'HIVE Integration Enabled' : 'HIVE Integration Disabled',
        description: enabled 
          ? 'Time entries will now sync with HIVE' 
          : 'HIVE synchronization has been disabled',
      });

      // If enabling, sync immediately
      if (enabled) {
        await syncHiveTimeEntries();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update HIVE integration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Expensify Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Receipt className="h-5 w-5" />
              <div>
                <CardTitle>Expensify Integration</CardTitle>
                <CardDescription>
                  Sync expense reports and receipts from Expensify
                </CardDescription>
              </div>
            </div>
            {expensifyEnabled ? (
              <Badge variant="success" className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Active
              </Badge>
            ) : (
              <Badge variant="secondary" className="flex items-center gap-1">
                <XCircle className="h-3 w-3" />
                Not Configured
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {expensifyEnabled ? (
            <>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Last Sync</p>
                  <p className="text-sm text-muted-foreground">
                    {lastExpensifySync 
                      ? format(lastExpensifySync, 'MMM d, yyyy h:mm a')
                      : 'Never synced'}
                  </p>
                </div>
                <Button
                  onClick={syncExpensifyExpenses}
                  disabled={syncing === 'expensify'}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {syncing === 'expensify' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Sync Now
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <DollarSign className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                  <p className="text-2xl font-bold text-blue-600">$0</p>
                  <p className="text-xs text-muted-foreground">Total Expenses</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-600" />
                  <p className="text-2xl font-bold text-green-600">0</p>
                  <p className="text-xs text-muted-foreground">Approved</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <AlertCircle className="h-5 w-5 mx-auto mb-1 text-yellow-600" />
                  <p className="text-2xl font-bold text-yellow-600">0</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-muted-foreground mb-4">
                Expensify integration is not configured for your organization
              </p>
              <Button variant="outline" size="sm" asChild>
                <a href="/integrations">Configure Expensify</a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* HIVE Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5" />
              <div>
                <CardTitle>HIVE Time Tracking</CardTitle>
                <CardDescription>
                  Sync time entries and project data with HIVE
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={hiveEnabled}
              onCheckedChange={toggleHiveIntegration}
              disabled={loading}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="hive-project-id">HIVE Project ID</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="hive-project-id"
                value={hiveProjectId}
                onChange={(e) => setHiveProjectId(e.target.value)}
                placeholder="Enter HIVE project ID"
                disabled={hiveEnabled}
              />
              {hiveProjectId && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(`https://app.hive.com/projects/${hiveProjectId}`, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {hiveEnabled && (
            <>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Last Sync</p>
                  <p className="text-sm text-muted-foreground">
                    {lastHiveSync 
                      ? format(lastHiveSync, 'MMM d, yyyy h:mm a')
                      : 'Never synced'}
                  </p>
                </div>
                <Button
                  onClick={syncHiveTimeEntries}
                  disabled={syncing === 'hive'}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {syncing === 'hive' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Sync Now
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <Clock className="h-5 w-5 mx-auto mb-1 text-purple-600" />
                  <p className="text-2xl font-bold text-purple-600">0h</p>
                  <p className="text-xs text-muted-foreground">Total Hours</p>
                </div>
                <div className="p-3 bg-indigo-50 rounded-lg">
                  <Calendar className="h-5 w-5 mx-auto mb-1 text-indigo-600" />
                  <p className="text-2xl font-bold text-indigo-600">0</p>
                  <p className="text-xs text-muted-foreground">Time Entries</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <DollarSign className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                  <p className="text-2xl font-bold text-blue-600">$0</p>
                  <p className="text-xs text-muted-foreground">Billable Value</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Integration Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Activity</CardTitle>
          <CardDescription>Recent synchronization events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm">No recent integration activity</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}